// Floating contextual toolbar for text elements.
//
// Mounts when a text layer is selected. Positioned above the element via
// requestAnimationFrame so it tracks scroll/zoom/page-switch without state
// thrashing. Auto-flips below the element when there's not enough room above
// the viewport, and clamps to the viewport edges horizontally.
//
// `isInteracting` (drag / resize / rotate) hides the toolbar immediately so it
// never overlaps a transform interaction (Figma/Canva behaviour). The toolbar
// re-appears the moment the interaction ends.
//
// All controls dispatch through onUpdateElement(id, partial) — same shape the
// right sidebar uses, so undo/redo and auto-save work identically.

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Bold, Italic, Underline, Strikethrough,
  AlignLeft, AlignCenter, AlignRight,
  MoreHorizontal, ChevronDown, Check, Search,
  List, ListOrdered,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { FONT_CATEGORIES, loadGoogleFont } from '../../lib/fonts';
import ElementQuickActions from './ElementQuickActions';

const Btn = ({ active, title, onClick, children, disabled }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    aria-pressed={!!active}
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'h-7 w-7 flex items-center justify-center rounded-md text-gray-600',
      'hover:text-gray-900 hover:bg-gray-100 transition-colors',
      active && 'bg-violet-50 text-violet-700 ring-1 ring-violet-200',
      disabled && 'opacity-30 hover:bg-transparent cursor-not-allowed'
    )}
  >
    {children}
  </button>
);

const Sep = () => <div className="w-px h-5 bg-gray-200 mx-0.5" />;

// Lazy-load a font when its option row enters view. Using IntersectionObserver
// instead of loading the whole catalog up-front keeps cold-start cheap (only
// the visible rows fetch a stylesheet) while still rendering each name in its
// own face once scrolled into view.
const FontRow = ({ name, active, onSelect }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          loadGoogleFont(name);
          io.disconnect();
          break;
        }
      }
    }, { root: el.closest('[data-font-list]') || null, rootMargin: '120px' });
    io.observe(el);
    return () => io.disconnect();
  }, [name]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm text-left',
        'hover:bg-gray-100 transition-colors',
        active && 'bg-violet-50 text-violet-700'
      )}
      style={{ fontFamily: `'${name}', sans-serif` }}
      title={name}
    >
      <span className="truncate">{name}</span>
      {active && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
    </button>
  );
};

const FontPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const current = value || 'Poppins';

  useEffect(() => { if (open) loadGoogleFont(current); }, [open, current]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FONT_CATEGORIES;
    return FONT_CATEGORIES
      .map((c) => ({ ...c, fonts: c.fonts.filter((f) => f.toLowerCase().includes(q)) }))
      .filter((c) => c.fonts.length > 0);
  }, [query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Font"
          className="h-7 bg-gray-100 rounded-md text-xs px-2 flex items-center gap-1.5 min-w-[140px] hover:bg-gray-200 transition-colors"
          style={{ fontFamily: `'${current}', sans-serif` }}
        >
          <span className="truncate flex-1 text-left">{current}</span>
          <ChevronDown className="w-3 h-3 flex-shrink-0 text-gray-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        className="w-64 p-2"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative mb-1.5">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search fonts"
            className="w-full h-7 pl-7 pr-2 text-xs bg-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-300"
          />
        </div>
        <div data-font-list className="max-h-72 overflow-y-auto pr-0.5">
          {filtered.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-4">No fonts match "{query}"</p>
          )}
          {filtered.map((cat) => (
            <div key={cat.id} className="mb-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-2 pt-1 pb-0.5">
                {cat.label}
              </p>
              {cat.fonts.map((f) => (
                <FontRow
                  key={f}
                  name={f}
                  active={f === current}
                  onSelect={() => { onChange(f); setOpen(false); }}
                />
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const TOOLBAR_OFFSET = 12;        // gap above element before flipping
const TOOLBAR_MIN_GAP = 8;        // safety margin from viewport edges
const APPROX_TOOLBAR_HEIGHT = 44;

// Read fontWeight robustly: numeric string, 'bold', 'normal', or undefined.
const isBold = (v) => {
  if (v == null) return false;
  const s = String(v).toLowerCase();
  if (s === 'bold' || s === 'bolder') return true;
  const n = parseFloat(s);
  return Number.isFinite(n) && n >= 600;
};

const TextFloatingToolbar = ({
  element,
  pageCanvasId,
  zoom,
  onUpdateElement,
  // Hide while the user is drag/resize/rotate-ing the element. Caller
  // (EditorPage) computes this from CanvasEditor's interaction state.
  isInteracting,
  // Optional multi-select union bbox in canvas coords {x,y,width,height}.
  // When provided, the toolbar anchors to this rect instead of the single
  // `element` so it sits above the entire selection.
  boundingBox,
  // Quick actions (shared with the image toolbar).
  onDuplicate,
  onDelete,
  onToggleLock,
  onLayerOrder,
}) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  const bboxKey = boundingBox
    ? `${boundingBox.x}|${boundingBox.y}|${boundingBox.width}|${boundingBox.height}`
    : null;

  // Position tracker — rAF loop while mounted. Cheap because we only read
  // getBoundingClientRect and call setState when the position actually moves.
  useEffect(() => {
    if (!element || element.type !== 'text') return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const canvas = document.getElementById(pageCanvasId);
      if (!canvas) {
        setPos(null);
        return;
      }
      const rect = canvas.getBoundingClientRect();

      const anchor = boundingBox || {
        x: element.x || 0,
        y: element.y || 0,
        width: element.width || 0,
        height: element.height || 0,
      };
      const elTop    = rect.top  + anchor.y * zoom;
      const elLeft   = rect.left + anchor.x * zoom;
      const elWidth  = anchor.width  * zoom;
      const elHeight = anchor.height * zoom;

      const toolbarW = ref.current?.offsetWidth || 460;
      const desiredLeft = elLeft + elWidth / 2 - toolbarW / 2;
      const left = Math.max(TOOLBAR_MIN_GAP,
        Math.min(window.innerWidth - toolbarW - TOOLBAR_MIN_GAP, desiredLeft));

      const placeAbove = elTop - APPROX_TOOLBAR_HEIGHT - TOOLBAR_OFFSET >= TOOLBAR_MIN_GAP;
      const top = placeAbove
        ? elTop - APPROX_TOOLBAR_HEIGHT - TOOLBAR_OFFSET
        : elTop + elHeight + TOOLBAR_OFFSET;

      setPos((prev) => {
        if (prev && Math.abs(prev.left - left) < 0.5 && Math.abs(prev.top - top) < 0.5) return prev;
        return { left, top };
      });
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return () => { cancelled = true; };
  }, [element?.id, element?.x, element?.y, element?.width, element?.height, pageCanvasId, zoom, element, bboxKey, boundingBox]);

  // Keyboard shortcuts — only fire when target isn't in a contentEditable / input.
  useEffect(() => {
    if (!element || element.type !== 'text') return;
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const t = e.target;
      const inEditable = t && (t.isContentEditable || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA');
      if (inEditable) return;
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          onUpdateElement(element.id, { fontWeight: isBold(element.fontWeight) ? '400' : '700' });
          break;
        case 'i':
          e.preventDefault();
          onUpdateElement(element.id, { fontStyle: element.fontStyle === 'italic' ? 'normal' : 'italic' });
          break;
        case 'u':
          e.preventDefault();
          onUpdateElement(element.id, {
            textDecoration: element.textDecoration === 'underline' ? 'none' : 'underline',
          });
          break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [element?.id, element?.fontWeight, element?.fontStyle, element?.textDecoration, onUpdateElement, element]);

  const setProp = useCallback((patch) => {
    if (!element) return;
    onUpdateElement(element.id, patch);
  }, [element, onUpdateElement]);

  // Hide-on-transform: bail before any DOM mounts so there is zero flicker.
  // The position rAF keeps running so when isInteracting flips back to false
  // the toolbar re-renders at the post-drag position immediately.
  if (!element || element.type !== 'text' || !pos || isInteracting) return null;

  const align = element.textAlign || 'left';
  const decoration = element.textDecoration || 'none';
  const isUnderlined = decoration.includes('underline');
  const isStriked    = decoration.includes('line-through');
  const toggleDecoration = (flag) => {
    const set = new Set(decoration === 'none' ? [] : decoration.split(/\s+/));
    if (set.has(flag)) set.delete(flag); else set.add(flag);
    setProp({ textDecoration: set.size === 0 ? 'none' : Array.from(set).join(' ') });
  };

  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label="Text formatting"
      data-testid="text-floating-toolbar"
      style={{
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        zIndex: 60,
        animation: 'editor-fade-scale-in 120ms ease-out',
      }}
      className={cn(
        'flex items-center gap-0.5 bg-white border border-gray-200 rounded-xl shadow-lg',
        'px-1.5 py-1 text-sm select-none'
      )}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <style>{`
        @keyframes editor-fade-scale-in {
          from { opacity: 0; transform: translateY(4px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)  scale(1);    }
        }
      `}</style>

      {/* Font family */}
      <FontPicker
        value={element.fontFamily || 'Poppins'}
        onChange={(v) => setProp({ fontFamily: v })}
      />

      {/* Font size */}
      <div className="flex items-center bg-gray-100 rounded-md h-7 px-1.5 ml-1" title="Size">
        <input
          type="number"
          min={4}
          value={String(element.fontSize ?? 16).replace('px', '')}
          onChange={(e) => setProp({ fontSize: `${e.target.value}px` })}
          className="w-10 bg-transparent text-sm text-gray-900 focus:outline-none tabular-nums"
        />
      </div>

      <Sep />

      {/* Bold / Italic */}
      <Btn
        active={isBold(element.fontWeight)}
        title="Bold (⌘B)"
        onClick={() => setProp({ fontWeight: isBold(element.fontWeight) ? '400' : '700' })}
      >
        <Bold className="w-3.5 h-3.5" />
      </Btn>
      <Btn
        active={element.fontStyle === 'italic'}
        title="Italic (⌘I)"
        onClick={() => setProp({ fontStyle: element.fontStyle === 'italic' ? 'normal' : 'italic' })}
      >
        <Italic className="w-3.5 h-3.5" />
      </Btn>

      <Sep />

      {/* Color */}
      <label className="relative h-7 w-7 rounded-md cursor-pointer ring-1 ring-gray-200 hover:ring-gray-300 overflow-hidden" title="Text color">
        <span className="absolute inset-1 rounded-sm" style={{ background: element.color || '#000000' }} />
        <input
          type="color"
          value={element.color || '#000000'}
          onChange={(e) => setProp({ color: e.target.value })}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
      </label>

      <Sep />

      {/* Align */}
      <Btn active={align === 'left'}   title="Align left"   onClick={() => setProp({ textAlign: 'left' })}>   <AlignLeft   className="w-3.5 h-3.5" /></Btn>
      <Btn active={align === 'center'} title="Align center" onClick={() => setProp({ textAlign: 'center' })}> <AlignCenter className="w-3.5 h-3.5" /></Btn>
      <Btn active={align === 'right'}  title="Align right"  onClick={() => setProp({ textAlign: 'right' })}>  <AlignRight  className="w-3.5 h-3.5" /></Btn>

      <Sep />

      {/* Lists */}
      <Btn
        active={element.listType === 'bullet'}
        title="Bullet list"
        onClick={() => setProp({ listType: element.listType === 'bullet' ? null : 'bullet' })}
      >
        <List className="w-3.5 h-3.5" />
      </Btn>
      <Btn
        active={element.listType === 'numbered'}
        title="Numbered list"
        onClick={() => setProp({ listType: element.listType === 'numbered' ? null : 'numbered' })}
      >
        <ListOrdered className="w-3.5 h-3.5" />
      </Btn>

      <Sep />

      {/* More popover — weight / decoration / opacity */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="More text controls"
            aria-label="More text controls"
            className={cn(
              'h-7 w-7 flex items-center justify-center rounded-md text-gray-600',
              'hover:text-gray-900 hover:bg-gray-100 transition-colors'
            )}
          >
            <MoreHorizontal className="w-3.5 h-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          sideOffset={8}
          className="w-[260px] p-3 space-y-3"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Weight</span>
            <select
              value={(() => {
                const w = element.fontWeight;
                if (w == null || w === '') return '400';
                const s = String(w).toLowerCase();
                if (s === 'normal' || s === 'regular') return '400';
                if (s === 'bold')    return '700';
                if (s === 'lighter') return '300';
                if (s === 'bolder')  return '800';
                return String(w);
              })()}
              onChange={(e) => setProp({ fontWeight: e.target.value })}
              className="h-7 bg-gray-100 rounded-md text-xs px-2 focus:outline-none"
            >
              <option value="300">Light</option>
              <option value="400">Regular</option>
              <option value="500">Medium</option>
              <option value="600">SemiBold</option>
              <option value="700">Bold</option>
              <option value="800">ExtraBold</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Decoration</span>
            <div className="flex gap-1">
              <Btn active={isUnderlined} title="Underline (⌘U)" onClick={() => toggleDecoration('underline')}>
                <Underline className="w-3.5 h-3.5" />
              </Btn>
              <Btn active={isStriked} title="Strikethrough" onClick={() => toggleDecoration('line-through')}>
                <Strikethrough className="w-3.5 h-3.5" />
              </Btn>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Opacity</span>
              <span className="text-[10px] text-gray-400 tabular-nums">
                {Math.round((element.opacity ?? 1) * 100)}%
              </span>
            </div>
            <input
              type="range" min={0} max={1} step={0.01}
              value={element.opacity ?? 1}
              onChange={(e) => setProp({ opacity: Number(e.target.value) })}
              className="w-full accent-violet-600"
            />
          </div>
        </PopoverContent>
      </Popover>
      <Sep />
      <ElementQuickActions
        element={element}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onToggleLock={onToggleLock}
        onLayerOrder={onLayerOrder}
      />
    </div>
  );
};

export default TextFloatingToolbar;
